import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

function BannerSucesso({ mensagem }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
      <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <p className="text-sm text-green-700">{mensagem}</p>
    </div>
  );
}

function BannerErro({ mensagem }) {
  if (!mensagem) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
      <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="text-sm text-red-700">{mensagem}</p>
    </div>
  );
}

function campoEnderecoVazio() {
  return { logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '', cep: '' };
}

function FormEmpresa({ empresa, onboardingId, readOnly, atualizarDados }) {
  const [emailCobranca, setEmailCobranca] = useState(empresa.emailCobranca || '');
  const [endereco, setEndereco] = useState({
    ...campoEnderecoVazio(),
    ...(empresa.endereco || {}),
  });
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');
  const [buscandoCep, setBuscandoCep] = useState(false);
  const timerSucesso = useRef(null);

  // Sincroniza se empresa mudar
  useEffect(() => {
    setEmailCobranca(empresa.emailCobranca || '');
    setEndereco({ ...campoEnderecoVazio(), ...(empresa.endereco || {}) });
  }, [empresa]);

  function atualizarEndereco(campo, valor) {
    setEndereco(prev => ({ ...prev, [campo]: valor }));
  }

  async function buscarCep(cep) {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const json = await resp.json();
      if (!json.erro) {
        setEndereco(prev => ({
          ...prev,
          logradouro: json.logradouro || prev.logradouro,
          bairro: json.bairro || prev.bairro,
          cidade: json.localidade || prev.cidade,
          uf: json.uf || prev.uf,
        }));
      }
    } catch {
      // falha silenciosa
    } finally {
      setBuscandoCep(false);
    }
  }

  async function handleSalvar(e) {
    e.preventDefault();
    if (readOnly) return;
    setSalvando(true);
    setSucesso('');
    setErro('');
    try {
      await api.put(`/onboardings/${onboardingId}/empresas/${empresa.id}`, {
        emailCobranca,
        endereco,
      });
      await atualizarDados();
      setSucesso('Dados da empresa salvos com sucesso.');
      if (timerSucesso.current) clearTimeout(timerSucesso.current);
      timerSucesso.current = setTimeout(() => setSucesso(''), 4000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={handleSalvar} className="space-y-5 pt-2">
      {sucesso && <BannerSucesso mensagem={sucesso} />}
      {erro && <BannerErro mensagem={erro} />}

      {/* Dados somente leitura da empresa */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">CNPJ</label>
          <input
            type="text"
            value={empresa.cnpj || '—'}
            readOnly
            className="input bg-gray-50 text-gray-500 cursor-default font-mono"
          />
        </div>
        <div>
          <label className="label">Nome</label>
          <input
            type="text"
            value={empresa.nome || '—'}
            readOnly
            className="input bg-gray-50 text-gray-500 cursor-default"
          />
        </div>
        {empresa.descricao && (
          <div className="sm:col-span-2">
            <label className="label">Descrição</label>
            <textarea
              value={empresa.descricao}
              readOnly
              rows={2}
              className="input bg-gray-50 text-gray-500 cursor-default resize-none"
            />
          </div>
        )}
      </div>

      {/* Divisor */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-sm font-medium text-gray-700 mb-4">Informações de cobrança e endereço</p>
      </div>

      {/* E-mail de cobrança */}
      <div>
        <label className="label">E-mail de cobrança</label>
        <input
          type="email"
          value={emailCobranca}
          onChange={e => setEmailCobranca(e.target.value)}
          disabled={readOnly}
          className="input"
          placeholder="financeiro@suaempresa.com.br"
        />
      </div>

      {/* Endereço */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label">CEP</label>
          <div className="relative">
            <input
              type="text"
              value={endereco.cep}
              onChange={e => atualizarEndereco('cep', e.target.value.replace(/\D/g, '').slice(0, 8))}
              onBlur={e => buscarCep(e.target.value)}
              disabled={readOnly}
              className="input"
              placeholder="00000-000"
              maxLength={8}
            />
            {buscandoCep && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Logradouro</label>
          <input
            type="text"
            value={endereco.logradouro}
            onChange={e => atualizarEndereco('logradouro', e.target.value)}
            disabled={readOnly}
            className="input"
            placeholder="Rua, Avenida, Travessa..."
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label">Número</label>
          <input
            type="text"
            value={endereco.numero}
            onChange={e => atualizarEndereco('numero', e.target.value)}
            disabled={readOnly}
            className="input"
            placeholder="123"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Complemento</label>
          <input
            type="text"
            value={endereco.complemento}
            onChange={e => atualizarEndereco('complemento', e.target.value)}
            disabled={readOnly}
            className="input"
            placeholder="Apto, Sala, Bloco (opcional)"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label">Bairro</label>
          <input
            type="text"
            value={endereco.bairro}
            onChange={e => atualizarEndereco('bairro', e.target.value)}
            disabled={readOnly}
            className="input"
            placeholder="Bairro"
          />
        </div>
        <div>
          <label className="label">Cidade</label>
          <input
            type="text"
            value={endereco.cidade}
            onChange={e => atualizarEndereco('cidade', e.target.value)}
            disabled={readOnly}
            className="input"
            placeholder="Cidade"
          />
        </div>
        <div>
          <label className="label">UF</label>
          <input
            type="text"
            value={endereco.uf}
            onChange={e => atualizarEndereco('uf', e.target.value.toUpperCase().slice(0, 2))}
            disabled={readOnly}
            className="input"
            placeholder="SP"
            maxLength={2}
          />
        </div>
      </div>

      {!readOnly && (
        <div className="flex justify-end pt-2">
          <button type="submit" disabled={salvando} className="btn-primary">
            {salvando ? 'Salvando...' : 'Salvar esta Empresa'}
          </button>
        </div>
      )}
    </form>
  );
}

function AccordionEmpresa({ empresa, onboardingId, readOnly, atualizarDados, indice }) {
  const [aberto, setAberto] = useState(indice === 0);

  return (
    <div className="card overflow-hidden p-0">
      {/* Cabeçalho do accordion */}
      <button
        type="button"
        onClick={() => setAberto(prev => !prev)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-brand-700">{indice + 1}</span>
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{empresa.nome || `Empresa ${indice + 1}`}</p>
            {empresa.cnpj && (
              <p className="text-xs text-gray-400 font-mono">{empresa.cnpj}</p>
            )}
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${aberto ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {aberto && (
        <div className="px-6 pb-6 border-t border-gray-100">
          <FormEmpresa
            empresa={empresa}
            onboardingId={onboardingId}
            readOnly={readOnly}
            atualizarDados={atualizarDados}
          />
        </div>
      )}
    </div>
  );
}

export default function Empresas({ dados, onboardingId, atualizarDados }) {
  const readOnly = !!dados.finalizado;
  const empresas = dados.empresas || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="section-title">Empresas (CNPJs)</h1>
        <p className="section-sub">
          Preencha o e-mail de cobrança e endereço para cada empresa vinculada ao seu contrato.
        </p>
      </div>

      {empresas.length === 0 ? (
        <div className="card text-center py-12 space-y-3">
          <div className="text-4xl">🏭</div>
          <p className="font-semibold text-gray-700">Nenhuma empresa cadastrada</p>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            Nenhuma empresa foi cadastrada pela HeadThink para este onboarding.
            Entre em contato com nosso suporte se precisar adicionar empresas.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {empresas.map((empresa, idx) => (
            <AccordionEmpresa
              key={empresa.id || idx}
              empresa={empresa}
              onboardingId={onboardingId}
              readOnly={readOnly}
              atualizarDados={atualizarDados}
              indice={idx}
            />
          ))}
        </div>
      )}
    </div>
  );
}
